import { Info } from "@mui/icons-material";
import { SortingState } from "@tanstack/react-table";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import LoadingSpinner from "components/LoadingSpinner";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import {
  fetchStockOverview,
  exportInStockCSV,
} from "slices/Inventory/Instock/StockOverview/thunk";
import { clearStockOverview } from "slices/Inventory/Instock/StockOverview/reducer";

type StockOverviewListingProps = {
  warehouseId: any;
};

// Selector for stock overview data from Redux
const selectStockOverview = createSelector(
  (state: any) => state.inventoryStockOverviewSlice,
  (item) => ({
    list: Array.isArray(item?.stockOverviewList) ? item.stockOverviewList : [],
    rowCount: item?.rowCount || 0,
    loading: item?.loading || false,
    error: item?.error || null,
  }),
);

const StockOverviewListing: React.FC<StockOverviewListingProps> = ({
  warehouseId,
}) => {
  document.title = "Stock Overview";

  const tableState = usePreservedTableState({
    key: TableKeys.INVENTORY.IN_STOCK.STOCK_OVERVIEW,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [search, setSearch] = useState("");
  // const [searchInput, setSearchInput] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [tableInstance, setTableInstance] = useState<any>();

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // Get data from Redux store
  const { list, rowCount, loading, error } = useSelector(selectStockOverview);

  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.inventory?.app_name,
    app_modules?.inventory?.modules?.in_stock,
  );

  // Fetch data callback
  const fetchData = useCallback(() => {
    if (warehouseId) {
      dispatch(
        fetchStockOverview({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
          warehouseId,
        }),
      );
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue,
    warehouseId,
  ]);

  const handleExport = async (): Promise<any> => {
    return await exportInStockCSV({
      search,
      filters: filterValue,
      warehouse_id: warehouseId,
    });
  };

  // Fetch data when dependencies change
  useEffect(() => {
    if (!warehouseId) {
      dispatch(clearStockOverview());
      return;
    }
    fetchData();
  }, [fetchData, warehouseId, dispatch]);

  // Debouncing for search input
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // Dynamic action column
  const DynamicAction = canView
    ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => {
            const data = row.original;
            const menuItems = [
              {
                icon: Info,
                label: "Item Details",
                permission: canView,
                link: (item: any) =>
                  `/inventory-item-details/${item.product_id}/${warehouseId}`,
                color: "primary",
              },
            ];
            return <DynamicMenuItem row={data} menuItems={menuItems} />;
          },
        },
      ]
    : [];

  // Table columns definition
  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row, table }: { row: any; table: any }) => {
          const pageIndex = table.getState().pagination.pageIndex;
          const pageSize = table.getState().pagination.pageSize;
          const rowIndex = row.index;
          return pageIndex * pageSize + rowIndex + 1;
        },
      },
      {
        header: "Products",
        accessorKey: "product_name",
        id: "product_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.INVENTORY?.IN_STOCK?.STOCKOVERVIEW?.FILTER_DROPDOWN(
            "product_name",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.product_name || "-",
      },
      {
        header: "Product SKU",
        accessorKey: "product.product_sku",
        id: "product_sku",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.INVENTORY?.IN_STOCK?.STOCKOVERVIEW?.FILTER_DROPDOWN(
            "product_sku",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) =>
          row.original.product?.product_sku || "-",
      },
      {
        header: "Category",
        accessorKey: "category",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.INVENTORY?.IN_STOCK?.STOCKOVERVIEW?.FILTER_DROPDOWN(
            "category",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.product?.category || "-",
      },
      {
        header: "Sub-Category",
        accessorKey: "product.sub_category",
        id: "sub_category",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.INVENTORY?.IN_STOCK?.STOCKOVERVIEW?.FILTER_DROPDOWN(
            "sub_category",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) =>
          row.original.product?.sub_category || "-",
      },
      {
        header: "Make",
        accessorKey: "product.make",
        id: "make",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.INVENTORY?.IN_STOCK?.STOCKOVERVIEW?.FILTER_DROPDOWN(
            "make",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.product?.make || "-",
      },
      {
        header: "Model",
        accessorKey: "product.model",
        id: "model",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.INVENTORY?.IN_STOCK?.STOCKOVERVIEW?.FILTER_DROPDOWN(
            "model",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.product?.model || "-",
      },
      {
        header: "Total Received Quantity",
        accessorKey: "total_stock",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => row.original.total_stock ?? 0,
      },
      {
        header: "Good Quantity",
        accessorKey: "robust_qty",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => row.original.robust_qty ?? 0,
      },
      {
        header: "Damaged Quantity",
        accessorKey: "damaged_qty",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => row.original.damaged_qty ?? 0,
      },
      {
        header: "Available Quantity",
        accessorKey: "warehouse_stock",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const totalStock = row.original.total_stock ?? 0;
          const packedQty = row.original.packed_qty ?? 0;
          const dispatchedQty = row.original.dispatched_qty ?? 0;
          const damagedQty = row.original.damaged_qty
          const warehouseStock = totalStock - damagedQty - packedQty - dispatchedQty;
          return warehouseStock;
        },
      },
      {
        header: "Packed Quantity",
        accessorKey: "packed_qty",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => row.original.packed_qty ?? 0,
      },
      {
        header: "Dispatched Quantity",
        accessorKey: "dispatched_qty",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => row.original.dispatched_qty ?? 0,
      },

      ...DynamicAction,
    ],
    [canView, warehouseId, DynamicAction],
  );

  return (
    <div
      className="d-flex flex-column"
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden",
      }}
    >
      <div className="flex-shrink-0">
        <Row className="align-items-center mecord-search-row g-2">
          <SearchBox
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search..."
          />

          <Col
            xs={12}
            md={12}
            lg={8}
            xl={7}
            className="d-flex justify-content-end align-items-center gap-2"
          >
            {tableInstance && (
              <ColumnVisibilityDropdown
                columns={tableInstance.getAllLeafColumns()}
                excludedColumns={["action"]}
              />
            )}
            {canView && <ExportButton onExport={handleExport} />}
          </Col>
        </Row>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: "100%",
          marginTop: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="text-center py-4 text-danger">
            <h5>Error: {error}</h5>
          </div>
        ) : (
          <LoaderErrorWrapper loading={loading} error={error}>
            <MasterTableContainer
              columns={columns}
              data={list || []}
              totalRecords={
                Number.isNaN(rowCount) || !rowCount ? 0 : Number(rowCount)
              }
              pageIndex={pageIndex}
              pageSize={pageSize}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              colValue={setFilterValue}
              sorting={sorting}
              onSortingChange={setSorting}
              isBordered={false}
              isPagination={true}
              manualSorting={true}
              onTableReady={setTableInstance}
            />
          </LoaderErrorWrapper>
        )}
      </div>
    </div>
  );
};

export default StockOverviewListing;
